/**
 * Server → client WebSocket frames for case chat.
 * @see AlphaCoreAIJava docs/chat-websocket-api.md
 */

export type ChatWsServerMessageType =
  | "SESSION_READY"
  | "MESSAGE_COMPLETE"
  | "ERROR";

export interface ChatWsSessionReadyMessage {
  type: "SESSION_READY";
  sessionId: string;
  caseId: number;
}

export interface ChatWsMessageCompleteMessage {
  type: "MESSAGE_COMPLETE";
  correlationId: string | null;
  message: string;
  detectedIntent: string | null;
  intentConfidence: number | null;
  suggestedFollowUps: string[] | null;
  sessionId: string | null;
  conversationId: string | null;
}

export interface ChatWsErrorMessage {
  type: "ERROR";
  correlationId: string | null;
  code: string | null;
  message: string | null;
}

export type ChatWsInboundServerMessage =
  | ChatWsSessionReadyMessage
  | ChatWsMessageCompleteMessage
  | ChatWsErrorMessage;

/** Client → server */
export interface ChatWsUserMessage {
  type: "USER_MESSAGE";
  correlationId: string;
  message: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

/**
 * Runtime validation for inbound WS frames. Returns null when the payload is
 * malformed or missing fields required for the declared message type.
 */
export function parseChatWsInboundMessage(
  data: unknown,
): ChatWsInboundServerMessage | null {
  if (!isRecord(data) || typeof data.type !== "string") {
    return null;
  }

  switch (data.type) {
    case "SESSION_READY": {
      if (!isNonEmptyString(data.sessionId)) return null;
      const caseId =
        typeof data.caseId === "number" && Number.isFinite(data.caseId)
          ? data.caseId
          : 0;
      return {
        type: "SESSION_READY",
        sessionId: data.sessionId,
        caseId,
      };
    }
    case "MESSAGE_COMPLETE": {
      if (!isNullableString(data.correlationId)) return null;
      if (typeof data.message !== "string") return null;
      return {
        type: "MESSAGE_COMPLETE",
        correlationId: data.correlationId,
        message: data.message,
        detectedIntent: isNullableString(data.detectedIntent)
          ? data.detectedIntent
          : null,
        intentConfidence:
          typeof data.intentConfidence === "number" &&
          Number.isFinite(data.intentConfidence)
            ? data.intentConfidence
            : null,
        suggestedFollowUps: Array.isArray(data.suggestedFollowUps)
          ? data.suggestedFollowUps.filter(
              (item): item is string => typeof item === "string",
            )
          : null,
        sessionId: isNullableString(data.sessionId) ? data.sessionId : null,
        conversationId: isNullableString(data.conversationId)
          ? data.conversationId
          : null,
      };
    }
    case "ERROR": {
      if (!isNullableString(data.correlationId)) return null;
      if (!isNullableString(data.code)) return null;
      if (!isNullableString(data.message)) return null;
      return {
        type: "ERROR",
        correlationId: data.correlationId,
        code: data.code,
        message: data.message,
      };
    }
    default:
      return null;
  }
}

/** @deprecated Prefer {@link parseChatWsInboundMessage} for inbound frames. */
export function isChatWsServerMessage(
  data: unknown,
): data is ChatWsInboundServerMessage {
  return parseChatWsInboundMessage(data) !== null;
}
